import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../hooks/useThemeColor";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel.jsx";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function UpdateMenu() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [menuName, setMenuName] = useState("");
  const [enableMenu, setEnableMenu] = useState(false);
  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [epos, setEpos] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [online, setOnline] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [menuGroups, setMenuGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [itemLookup, setItemLookup] = useState(() => new Map());
  const [menuOrderby, setMenuOrderBy] = useState(0);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);
  const [schedules, setSchedules] = useState(schedule);
  const [savedData, setSavedData] = useState({
    commonTimeSlot: [{ from: "00:00", to: "23:59" }],
    schedules: schedule,
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      menuName: menuName || "",
      enableMenu: Boolean(enableMenu),
      regularHours: Boolean(regularHours),
      epos: Boolean(epos),
      kiosk: Boolean(kiosk),
      online: Boolean(online),
      delivery: Boolean(delivery),
      menuOrderby,
      useSameTimeSlots: Boolean(useSameTimeSlots),
      commonTimeSlot,
      schedules,
      menuGroups: (menuGroups || []).map((g) => ({
        menuGroupID: g?.menuGroupID || 0,
        menuGroup_Name: g?.menuGroup_Name || "",
        menuGroup_Publish: Boolean(g?.menuGroup_Publish),
        menuGroup_Orderby: g?.menuGroup_Orderby ?? 0,
        selectedProduct: (g?.selectedProduct || []).map(
          (p) => p?.menuItem_ID ?? p
        ),
      })),
    });
  }, [
    menuName,
    enableMenu,
    regularHours,
    epos,
    kiosk,
    online,
    delivery,
    menuOrderby,
    useSameTimeSlots,
    commonTimeSlot,
    schedules,
    menuGroups,
  ]);

  useEffect(() => {
    if (initialLoading) return;
    if (initialFormSnapshotRef.current !== null) return;
    // Defer so product enrichment can settle after both fetches
    const timer = setTimeout(() => {
      if (initialFormSnapshotRef.current === null) {
        initialFormSnapshotRef.current = getFormSnapshot();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [initialLoading, getFormSnapshot]);

  const isDirty =
    !initialLoading &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const getProductId = useCallback((p) => {
    // Selected products sometimes come as plain IDs
    if (typeof p === "number") return Number.isFinite(p) ? p : null;
    if (typeof p === "string") {
      const n = Number(p);
      return Number.isFinite(n) ? n : null;
    }

    const id =
      p?.menuItem_ID ??
      p?.MenuItem_ID ??
      p?.menuItemId ??
      p?.menuItemID ??
      p?.MenuItemID ??
      p?.id;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }, []);

  const productsById = itemLookup;

  const enrichSelectedProduct = useCallback(
    (p) => {
      const id = getProductId(p);
      if (id == null) return p;
      const full = productsById.get(String(id));
      // If we only have an ID, normalize to object so UI can render consistently
      if (!full) {
        return typeof p === "object" && p !== null
          ? { ...p, menuItem_ID: p?.menuItem_ID ?? id }
          : { menuItem_ID: id };
      }

      return {
        ...(typeof p === "object" && p !== null ? p : {}),
        menuItem_ID:
          (typeof p === "object" && p !== null ? p?.menuItem_ID : null) ??
          full?.menuItem_ID ??
          id,
        menuItem_Name:
          (typeof p === "object" && p !== null ? p?.menuItem_Name : null) ??
          full?.menuItem_Name ??
          full?.name ??
          "",
        menuItem_Image:
          (typeof p === "object" && p !== null ? p?.menuItem_Image : null) ??
          full?.menuItem_Image ??
          full?.image ??
          "",
      };
    },
    [getProductId, productsById]
  );

  const enrichSelectedProducts = useCallback(
    (list) => (Array.isArray(list) ? list.map(enrichSelectedProduct) : []),
    [enrichSelectedProduct]
  );

  // Fetch menu details and products (unchanged)
  const fetchMenuDetails = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(APIs.GET_MENU_BY_MENU_ID, {
        params: { CompID, MenuID: menuId },
      });
      // Menu Details Response logged

      const menuDetails = response.data.data || response.data;
      // Menu Details logged
   
      const nextGroups = menuDetails.menuGroups.map((item, idx) => ({
        menuGroupID: item.menuGroupID,
        menu_ID: parseInt(item.menu_ID),
        localizeID: item.localizeID,
        compID: item.compID,
        menuGroup_Name: item.menuGroup_Name,
        menuGroup_Publish: item.menuGroup_Publish,
        menuGroup_Orderby: item.menuGroup_Orderby || idx,
        updateDateTime: new Date().toISOString(),
        expanded: false,
        // Some APIs return only IDs in `products` for menu groups; enrich later via ByIds
        selectedProduct: item.products || [],
      }));
      setMenuGroups(nextGroups);
      const productIds = nextGroups.flatMap((g) =>
        (g.selectedProduct || []).map((p) => getProductId(p)).filter((id) => id != null)
      );
      // Fire-and-forget hydration for chip labels
      (async () => {
        try {
          const response = await axiosInstance.get(APIs.GET_MENU_ITEMS_BY_IDS, {
            params: { CompID, Ids: [...new Set(productIds)].join(",") },
          });
          const data = response.data?.data || [];
          if (Array.isArray(data) && data.length) {
            setItemLookup((prev) => {
              const next = new Map(prev);
              data.forEach((p) => {
                const id = getProductId(p);
                if (id == null) return;
                next.set(String(id), {
                  ...p,
                  menuItem_ID: id,
                  MenuItem_ID: id,
                  menuItem_Name: p.menuItem_Name || p.MenuItem_Name || p.name || "",
                  menuItem_Image: p.menuItem_Image || p.MenuItem_Image || "",
                });
              });
              return next;
            });
          }
        } catch (_) {
          /* ignore */
        }
      })();

      setEpos(menuDetails.isActiveforTill);
      setOnline(menuDetails.isActiveforTakeAway);
      setKiosk(menuDetails.isActiveforKIOSK);
      setDelivery(menuDetails.isActiveforDelivery);
      setMenuName(menuDetails.menu_Name || "");
      setEnableMenu(menuDetails.menu_Publish);
      setRegularHours(!menuDetails.isAvailableInTimeIntervals);
      setMenuOrderBy(menuDetails.menu_Orderby);
     
      if (
        menuDetails.menuWeeklyTimings &&
        Array.isArray(menuDetails.menuWeeklyTimings)
      ) {
        const isCommon = checkIfCommonTimeSlots(menuDetails.menuWeeklyTimings);
        if (isCommon) {
          const firstDaySlots = menuDetails.menuWeeklyTimings
            .filter(
              (day) => day.dayName === menuDetails.menuWeeklyTimings[0].dayName
            )
            .map((day) => ({
              from: day.openTime,
              to: day.closeTime,
            }));

          setCommonTimeSlot(firstDaySlots);
        } else {
          setUseSameTimeSlots(false);
           
          const convertedSchedules = convertToScheduleFormat(menuDetails.menuWeeklyTimings);
          setSchedules(convertedSchedules);
        }
      }
    } catch (err) {
      // Error fetching menu details silently
    } finally {
      setLoading(false);
    }
  }, [CompID, menuId]);

  const mergeItemLookup = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    setItemLookup((prev) => {
      const next = new Map(prev);
      list.forEach((p) => {
        const id = getProductId(p);
        if (id == null || !Number.isFinite(id)) return;
        next.set(String(id), {
          ...p,
          menuItem_ID: id,
          MenuItem_ID: id,
          menuItem_Name: p.menuItem_Name || p.MenuItem_Name || p.name || "",
          menuItem_Image: p.menuItem_Image || p.MenuItem_Image || "",
        });
      });
      return next;
    });
  }, [getProductId]);

  const hydrateProductsByIds = useCallback(
    async (ids) => {
      const unique = [
        ...new Set(
          (ids || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
            .map(String)
        ),
      ];
      if (!CompID || unique.length === 0) return;
      try {
        const response = await axiosInstance.get(APIs.GET_MENU_ITEMS_BY_IDS, {
          params: { CompID, Ids: unique.join(",") },
        });
        const data = response.data?.data || [];
        if (Array.isArray(data)) mergeItemLookup(data);
      } catch (error) {
        // Error logged
      }
    },
    [CompID, mergeItemLookup]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchMenuDetails();
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMenuDetails]);

  // Enrich selected products with name/image for display.
  // Important: products & menu details can load in any order, so we re-run when either changes.
  useEffect(() => {
    if (!productsById.size) return;

    setMenuGroups((prevGroups) => {
      let changed = false;

      const nextGroups = (Array.isArray(prevGroups) ? prevGroups : []).map((g) => {
        const current = Array.isArray(g.selectedProduct) ? g.selectedProduct : [];
        const enriched = current.map(enrichSelectedProduct);

        // Detect whether enrichment changed anything (avoid infinite loops)
        if (!changed) {
          for (let i = 0; i < current.length; i++) {
            const before = current[i];
            const after = enriched[i];

            const beforeId = getProductId(before);
            const afterId = getProductId(after);
            if (beforeId !== afterId) {
              changed = true;
              break;
            }

            const beforeName =
              typeof before === "object" && before !== null ? before.menuItem_Name : undefined;
            const beforeImg =
              typeof before === "object" && before !== null ? before.menuItem_Image : undefined;

            if (
              (beforeName == null && after?.menuItem_Name) ||
              (beforeImg == null && after?.menuItem_Image)
            ) {
              changed = true;
              break;
            }
          }
        }

        return { ...g, selectedProduct: enriched };
      });

      return changed ? nextGroups : prevGroups;
    });
  }, [productsById, menuGroups.length, enrichSelectedProduct, getProductId]);

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Allow dropping
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newMenuGroups = [...menuGroups];
    const [draggedItem] = newMenuGroups.splice(draggedIndex, 1); // Remove dragged item
    newMenuGroups.splice(dropIndex, 0, draggedItem); // Insert at new position

    // Update menuGroup_Orderby for all items
    const updatedMenuGroups = newMenuGroups.map((group, idx) => ({
      ...group,
      menuGroup_Orderby: idx,
    }));

    setMenuGroups(updatedMenuGroups);
    setDraggedIndex(null);

  };

  const handleDragEnd = () => {
    setDraggedIndex(null); // Reset dragged index
  };

  const handleMenuGroupChange = (index, value) => {
    setMenuGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, menuGroup_Name: value } : group
      )
    );
  };

  const handleAddMenuGroup = () => {
    setMenuGroups([
      ...menuGroups,
      {
        menuGroupID: 0,
        menu_ID: parseInt(menuId),
        localizeID: "en",
        compID: CompID,
        menuGroup_Name: "",
        menuGroup_Publish: true,
        menuGroup_Orderby: menuGroups.length, // Set order as last item
        updateDateTime: new Date().toISOString(),
        expanded: false,
        selectedProduct: [],
      },
    ]);
  };

  const handleDeleteMenuGroup = (index) => {
    const newMenuGroups = menuGroups
      .filter((_, i) => i !== index)
      .map((group, idx) => ({
        ...group,
        menuGroup_Orderby: idx, // Reorder after deletion
      }));
    setMenuGroups(newMenuGroups); // Update order after deletion
  };

  const toggleGroupExpansion = (index) => {
    setMenuGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const handleAddProduct = (groupIndex) => {
    setCurrentGroupIndex(groupIndex);
    setSelectedProducts(enrichSelectedProducts(menuGroups[groupIndex].selectedProduct || []));
    setShowModal(true);
  };

  const handleProductSelectionSubmit = (selectedProducts) => {
    if (currentGroupIndex !== null) {
      setMenuGroups((prevGroups) =>
        prevGroups.map((group, index) =>
          index === currentGroupIndex
            ? { ...group, selectedProduct: enrichSelectedProducts(selectedProducts) }
            : group
        )
      );
    }
    setShowModal(false);
    setCurrentGroupIndex(null);
  };

  const applyCommonTimeSlotToAll = () => {
    // Ensure commonTimeSlot is treated as an array
    const slotArray = Array.isArray(commonTimeSlot) ? commonTimeSlot : [];

    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: slotArray.map((slot) => ({ ...slot })),
    }));

    setSchedules(updatedSchedules);
    setSavedData((prev) => ({ ...prev, schedules: updatedSchedules }));
  };

  const handleRegularHoursToggle = () => {
    const newRegularHours = !regularHours;
    setRegularHours(newRegularHours);
    if (!newRegularHours) {
      setSchedules(savedData.schedules);
      setCommonTimeSlot(savedData.commonTimeSlot);
    } else {
      setSavedData({
        commonTimeSlot: [...commonTimeSlot],
        schedules: [...schedules],
      });
    }
  };

  const handleSameTimeSlotsToggle = () => {
    const newUseSameTimeSlots = !useSameTimeSlots;
    setUseSameTimeSlots(newUseSameTimeSlots);
    if (newUseSameTimeSlots) {
      setSavedData((prev) => ({ ...prev, schedules: [...schedules] }));
      applyCommonTimeSlotToAll();
    } else {
      if (savedData.schedules.some((day) => day.timeSlots.length > 0)) {
        setSchedules(savedData.schedules);
      }
    }
  };

  const handleUpdate = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!menuName.trim()) {
      showErrorToast("Menu name is required");
      return false;
    }

    // Check if at least one menu group has a name
    const validGroups = menuGroups.filter((group) => group.menuGroup_Name?.trim().length > 0);
    if (validGroups.length === 0) {
      showErrorToast("Please add at least one menu group with a name.");
      return false;
    }

    setLoading(true);

    const formattedSchedules = [];

    if (!regularHours) {
      if (useSameTimeSlots) {
        const days = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ];
        days.forEach((day) => {
          // If there are time slots, add them; otherwise, explicitly set an empty array
          if (commonTimeSlot.length > 0) {
            commonTimeSlot.forEach((slot) => {
              formattedSchedules.push({
                ID: 0,
                CompID: CompID,
                Menu_ID: parseInt(menuId),
                DayName: day,
                OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm A"),
                CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm A"),
                UpdateDateTime: new Date().toISOString(),
              });
            });
          }
        });
      } else {
        // Process each day of the schedule separately
        schedules.forEach((schedule) => {
          if (schedule.checked && schedule.timeSlots.length > 0) {
            // Process checked days with time slots
            schedule.timeSlots.forEach((slot) => {
              if (
                moment(slot.from, ["h:mm A"]).isValid() &&
                moment(slot.to, ["h:mm A"]).isValid()
              ) {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  Menu_ID: parseInt(menuId),
                  DayName: schedule.day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              }
            });
          }
        });
      }
    }

    const payload = {
      Menu_ID: parseInt(menuId),
      CompID: CompID,
      Menu_Name: menuName,
      Menu_Publish: enableMenu,
      Menu_Orderby: menuOrderby,
      IsActiveforTill: epos,
      IsActiveforKIOSK: kiosk,
      IsActiveforTakeAway: online,
      IsActiveforDelivery: delivery,
      IsAvailableInTimeIntervals: !regularHours,
      UpdateDateTime: new Date().toISOString(),
      MenuWeeklyTimings: formattedSchedules,
      MenuGroups: menuGroups.map((group) => ({
        MenuGroupID: group.menuGroupID,
        Menu_ID: group.menu_ID,
        LocalizeID: group.localizeID,
        CompID: group.compID,
        MenuGroup_Name: group.menuGroup_Name,
        MenuGroup_Publish: group.menuGroup_Publish,
        MenuGroup_Orderby: group.menuGroup_Orderby,
        UpdateDateTime: group.updateDateTime,
        products: group.selectedProduct.map((product) => ({
          ...product,
          Allergens: "allergens",
        })),
      })),
    };

    try {
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_MENU_AND_MENU_GROUP,
        payload
      );
      if (response.data.status) {
        showSuccessToast("Menu updated successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          navigate("/menu/manageMenu");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update menu");
        return false;
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating menu");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleUpdate({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/menu/manageMenu">Manage Menu</Link>
            </li>
            <li className="breadcrumb-item active">Update Menu</li>
          </ol>
          <h1 className="page-header mb-0">Update Menu</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/menu/manageMenu")}
          onSave={() => handleUpdate({ exitAfterSave: false })}
          onSaveAndExit={() => handleUpdate({ exitAfterSave: true })}
          saving={loading}
          disabled={initialLoading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <div
                className="p-4 rounded-3 shadow-sm"
                style={{
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  border: isDarkMode ? "1px solid #404040" : undefined,
                  color: isDarkMode ? "#ffffff" : undefined,
                }}
              >
                <div className="row mb-4 align-items-center">
                  <div className="col-md-6">
                    <FormInput
                      label="Menu Name *"
                      type="text"
                      value={menuName}
                      onchange={(e) => setMenuName(e.target.value)}
                      placeHolder="Enter menu name"
                      name="menuName"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="row row-cols-2 g-3">
                      {[
                        { label: "Enable Menu", checked: enableMenu, toggle: () => setEnableMenu(!enableMenu) },
                        { label: "KIOSK", checked: kiosk, toggle: () => setKiosk(!kiosk) },
                        { label: "Online Orders", checked: online, toggle: () => setOnline(!online) },
                        { label: "Delivery", checked: delivery, toggle: () => setDelivery(!delivery) },
                        { label: "E-POS", checked: epos, toggle: () => setEpos(!epos) },
                      ].map(({ label, checked, toggle }) => (
                        <div key={label} className="col">
                          <ToggleSwitch
                            isChecked={checked}
                            onToggle={toggle}
                            label={label}
                            themeColor={themeColor}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-2">
                  <div className="col-12">
                    <div
                      className="card p-3"
                      style={{
                        backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <h5 className="card-title mb-3">Time Scheduling</h5>
                      <div className="row g-3 d-flex align-items-center p-4">
                        <div className="col-6">
                          <ToggleSwitch
                            isChecked={regularHours}
                            onToggle={handleRegularHoursToggle}
                            label="Available in regular business hours"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      {!regularHours && (
                        <>
                          <hr />
                          <div className="row g-3 d-flex align-items-center p-4">
                            <div className="col-6">
                              <ToggleSwitch
                                isChecked={useSameTimeSlots}
                                onToggle={handleSameTimeSlotsToggle}
                                label="Use same time slots for all days"
                                themeColor={themeColor}
                              />
                            </div>
                          </div>
                          <hr />
                          {useSameTimeSlots ? (
                            <CommonTimeSlotForAllWeek
                              setCommonTimeSlots={(slots) => {
                                const slotArray = Array.isArray(slots) ? slots : [];
                                setCommonTimeSlot(slotArray);
                                setSavedData((prev) => ({ ...prev, commonTimeSlot: slotArray }));
                                setSchedules((prev) =>
                                  prev.map((s) => ({ ...s, timeSlots: slotArray.map((slot) => ({ ...slot })) }))
                                );
                              }}
                              commonTimeSlots={commonTimeSlot}
                              isProfilePage={false}
                            />
                          ) : (
                            <ScheduleManager
                              schedules={schedules}
                              setSchedules={(updatedSchedules) => {
                                const next =
                                  typeof updatedSchedules === "function"
                                    ? updatedSchedules(schedules)
                                    : updatedSchedules;
                                const safe = Array.isArray(next) ? next : schedules;
                                setSchedules(safe);
                                setSavedData((prev) => ({ ...prev, schedules: safe }));
                              }}
                              isProfilePage={false}
                              isDarkMode={isDarkMode}
                              themeColor={themeColor}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-4 mt-2">
                  <div className="col-12">
                    <div
                      className="card p-3"
                      style={{
                        backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <h5 className="card-title mb-1">Menu Groups</h5>
                      <p
                        className="small mb-3"
                        style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
                      >
                        Add groups, assign items, and drag to reorder.
                      </p>
                      {menuGroups.map((group, index) => (
                        <div
                          key={index}
                          className="mb-3 rounded-3 p-3"
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          style={{
                            opacity: draggedIndex === index ? 0.7 : 1,
                            cursor: "grab",
                            backgroundColor: isDarkMode ? "#1e2428" : "#f8f9fa",
                            border: isDarkMode
                              ? "1px solid #404040"
                              : "1px solid #e9ecef",
                            color: isDarkMode ? "#ffffff" : undefined,
                          }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="fas fa-grip-vertical me-2"
                              style={{
                                cursor: "grab",
                                color: isDarkMode ? "#adb5bd" : "#6c757d",
                              }}
                            />
                            <i
                              className={`fas fa-chevron-${group.expanded ? "down" : "right"} me-2`}
                              style={{
                                cursor: "pointer",
                                color: themeColor,
                              }}
                              onClick={() => toggleGroupExpansion(index)}
                            />
                            <div className="flex-grow-1">
                              <FormInput
                                placeHolder={`Group ${index + 1} (e.g., Appetizers, Main Course)`}
                                type="text"
                                value={group.menuGroup_Name}
                                onchange={(e) =>
                                  handleMenuGroupChange(index, e.target.value)
                                }
                              />
                            </div>
                            {menuGroups.length > 1 && (
                              <i
                                className="fas fa-trash text-danger ms-2"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDeleteMenuGroup(index)}
                              />
                            )}
                          </div>
                          {group.expanded && (
                            <div
                              className="mt-3 p-3 rounded-3"
                              style={{
                                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                                border: isDarkMode
                                  ? `1px solid ${themeColor}33`
                                  : `1px solid ${themeColor}22`,
                              }}
                            >
                              {group.selectedProduct &&
                              group.selectedProduct.length > 0 ? (
                                <>
                                  <h6
                                    className="mb-2"
                                    style={{
                                      color: isDarkMode ? "#ffffff" : "#212529",
                                    }}
                                  >
                                    Selected Products
                                  </h6>
                                  {group.selectedProduct.map(
                                    (product, prodIndex) => (
                                      <div
                                        key={prodIndex}
                                        className="d-flex justify-content-between align-items-center mb-2 p-2 rounded-3"
                                        style={{
                                          backgroundColor: isDarkMode
                                            ? "#1e2428"
                                            : "#f8f9fa",
                                          border: isDarkMode
                                            ? "1px solid #404040"
                                            : "1px solid #e9ecef",
                                          color: isDarkMode
                                            ? "#ffffff"
                                            : undefined,
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          {product.menuItem_Image ? (
                                            <img
                                              src={product.menuItem_Image}
                                              alt={product.menuItem_Name}
                                              className="rounded me-2"
                                              style={{
                                                width: 32,
                                                height: 32,
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className="rounded me-2 d-flex align-items-center justify-content-center"
                                              style={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: isDarkMode
                                                  ? "#343a40"
                                                  : "#e9ecef",
                                                color: isDarkMode
                                                  ? "#adb5bd"
                                                  : "#6c757d",
                                              }}
                                            >
                                              <i
                                                className="fa fa-utensils"
                                                style={{ fontSize: 12 }}
                                              />
                                            </div>
                                          )}
                                          <span>{product.menuItem_Name}</span>
                                        </div>
                                        <i
                                          className="fas fa-trash text-danger"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            setMenuGroups((prev) =>
                                              prev.map((g, i) =>
                                                i === index
                                                  ? {
                                                      ...g,
                                                      selectedProduct:
                                                        g.selectedProduct.filter(
                                                          (_, pIdx) =>
                                                            pIdx !== prodIndex
                                                        ),
                                                    }
                                                  : g
                                              )
                                            );
                                          }}
                                        />
                                      </div>
                                    )
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-sm mt-2"
                                    onClick={() => handleAddProduct(index)}
                                    style={{
                                      backgroundColor: `${themeColor}18`,
                                      border: `1px solid ${themeColor}`,
                                      color: themeColor,
                                      borderRadius: 8,
                                      fontWeight: 600,
                                    }}
                                  >
                                    <i className="fas fa-plus me-1"></i>
                                    Add More Items
                                  </button>
                                </>
                              ) : (
                                <div className="text-center py-3">
                                  <div
                                    className="d-inline-flex align-items-center justify-content-center mb-2 rounded-3"
                                    style={{
                                      width: 48,
                                      height: 48,
                                      backgroundColor: `${themeColor}18`,
                                      border: `1px solid ${themeColor}40`,
                                      color: themeColor,
                                    }}
                                  >
                                    <i className="fa fa-box" />
                                  </div>
                                  <p
                                    className="small mb-3"
                                    style={{
                                      color: isDarkMode ? "#adb5bd" : "#6c757d",
                                    }}
                                  >
                                    No items in this group.
                                  </p>
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => handleAddProduct(index)}
                                    style={{
                                      backgroundColor: themeColor,
                                      border: `1px solid ${themeColor}`,
                                      color: "#ffffff",
                                      borderRadius: 8,
                                      fontWeight: 600,
                                      padding: "6px 14px",
                                    }}
                                  >
                                    <i className="fas fa-plus me-1"></i>
                                    Add Item
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn"
                        onClick={handleAddMenuGroup}
                        style={{
                          backgroundColor: isDarkMode
                            ? `${themeColor}18`
                            : `${themeColor}12`,
                          border: `1px solid ${themeColor}`,
                          color: themeColor,
                          borderRadius: 8,
                          fontWeight: 600,
                        }}
                      >
                        <i className="fas fa-plus me-2"></i>Add Group
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>
      </GridSectionLoader>
      <ProductMultiSelectionModal
        showModal={showModal}
        setShowModal={setShowModal}
        showButton={true}
        onSubmit={(selected) => {
          mergeItemLookup(Array.isArray(selected) ? selected : []);
          handleProductSelectionSubmit(selected);
        }}
        setSelectedProducts={setSelectedProducts}
        selectedProducts={selectedProducts}
      />
    </div>
  );
}

export default UpdateMenu;